import moment from "moment";
import { createKey } from "../../../Shared/utils/createKey";
import { ActionTypes } from "../../OrderFlowProvider/OrderFlowActionTypes";
import { RecipeIngredientCompatibility, RecipeSelectionType, } from "../../OrderFlowProvider/OrderFlowTypes";
import { removeDuplicatesFromArray } from "../Session/sessionUtils";
import { LogLevel } from "../../../Shared/hooks/useLogging/Logging-types";
/**
 * None ingredient does not have an ID but an ID is required to use the customization form.
 * We will use this ID for customization BUT it will be set back to null before saving the cart
 */
export const NONE_INGREDIENT_ID = -1;
/**
 *  Called when the user makes meal selections. Recipes are fetched based on their meals, omissions
 *  and recipe type. The response contains:
 *  RecipeInfo - A list of recipes with necessary data;
 *  RankedRecipes - A list of recipes with a changeRank;
 *  OrderModel - The auto selected recipes and data about the order;
 *
 *  Recipes are sorted by their changeRank
 *
 * @param dispatch
 * @param getSelectedRecipes
 * @param cartMeals
 * @param payload
 * @returns
 */
export const getRecipes = (dispatch, getSelectedRecipes, datadogLog) => (recipeFetchFlags, cartMeals, payload) => {
    dispatch({ type: ActionTypes.SET_SELECTED_RECIPES, payload: [] });
    dispatch({ type: ActionTypes.SET_MEAL_SELECTION_STATUS, payload: { completed: false } });
    dispatch({ type: ActionTypes.SET_RECIPE_FETCH_STATUS, payload: { completed: false, error: false } });
    getSelectedRecipes(payload)
        .then((data) => {
        const { success, selectedRecipeModel } = data;
        const { OrderModel: orderModel, RankedRecipes: rankedRecipes, RecipeInfo: recipeInfo } = selectedRecipeModel;
        const { shouldAutoSelectRecipes, shouldReplaceIngredients } = recipeFetchFlags;
        if (!success) {
            datadogLog("Error fetching recipes", LogLevel.ERROR);
            dispatch({ type: ActionTypes.SET_RECIPE_FETCH_STATUS, payload: { completed: true, error: true } });
            return;
        }
        if (!rankedRecipes.length || !recipeInfo.length) {
            dispatch({ type: ActionTypes.SET_RECIPE_FETCH_STATUS, payload: { completed: true, error: false } });
            dispatch({ type: ActionTypes.SET_MEAL_SELECTION_STATUS, payload: { completed: true } });
            return;
        }
        const sortedRankedRecipes = rankedRecipes.sort((a, b) => a.changeRank - b.changeRank);
        const rankedRecipesWithInfo = [];
        //Favorite recipes will always have their DayOfWeek set
        const customerFavorites = sortedRankedRecipes.filter((rr) => rr.DayOfWeek !== null);
        sortedRankedRecipes.forEach((rankedRecipe) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
            const currentRecipeInfo = recipeInfo === null || recipeInfo === void 0 ? void 0 : recipeInfo.find((recipe) => recipe.RecipeId === rankedRecipe.recipeId);
            if (!currentRecipeInfo) {
                return;
            }
            const recipeIngredientActions = rankedRecipe.recipeIngredientActions;
            let assignments = currentRecipeInfo === null || currentRecipeInfo === void 0 ? void 0 : currentRecipeInfo.Assignments;
            //Recurring meal date is being used so create an Assignment for each day of the order
            if (currentRecipeInfo === null || currentRecipeInfo === void 0 ? void 0 : currentRecipeInfo.Assignments.some((assignment) => assignment.Date === null)) {
                const orderDays = [...new Set(orderModel.Meals.map((meal) => meal.MealDate))];
                const newAssignments = [];
                const { automaticReplacements, recipeIngredientConflicts, recipeIngredientSkips, compatibilityType } = checkReplacementsAndConflicts(recipeIngredientActions, shouldReplaceIngredients, currentRecipeInfo, payload.Preferences.map((p) => p.IngredientMajorID));
                currentRecipeInfo === null || currentRecipeInfo === void 0 ? void 0 : currentRecipeInfo.Assignments.forEach((recipeAssignment) => {
                    orderDays.forEach((day) => {
                        var _a, _b;
                        const assignment = {
                            Date: moment(day),
                            Quantity: (_a = recipeAssignment === null || recipeAssignment === void 0 ? void 0 : recipeAssignment.Quantity) !== null && _a !== void 0 ? _a : 0,
                            RecipeId: currentRecipeInfo.RecipeId,
                            RecipeName: currentRecipeInfo.RecipeName,
                            RecipeShortName: currentRecipeInfo.RecipeShortName,
                            RecipePrice: (_b = currentRecipeInfo.RecipePrice) !== null && _b !== void 0 ? _b : 0,
                            SelectedIngredientOptions: [],
                            AutomaticReplacements: automaticReplacements,
                            ProgramMealId: recipeAssignment.ProgramMealId,
                            IsAutomaticSelection: false,
                            RecipeAssignmentPrice: recipeAssignment === null || recipeAssignment === void 0 ? void 0 : recipeAssignment.RecipeAssignmentPrice,
                            CompatibilityType: compatibilityType,
                            Conflicts: recipeIngredientConflicts,
                            Skips: recipeIngredientSkips,
                            AvailableQuantity: recipeAssignment === null || recipeAssignment === void 0 ? void 0 : recipeAssignment.AvailableQuantity,
                            AssignmentId: createKey(),
                        };
                        newAssignments.push(assignment);
                    });
                });
                assignments = newAssignments;
            }
            const { automaticReplacements, recipeIngredientConflicts, recipeIngredientSkips, compatibilityType } = checkReplacementsAndConflicts(recipeIngredientActions, shouldReplaceIngredients, currentRecipeInfo, payload.Preferences.map((p) => p.IngredientMajorID));
            //If the recipe is a favorite, remove assignments for other days
            if (rankedRecipe.DayOfWeek !== null) {
                assignments = assignments === null || assignments === void 0 ? void 0 : assignments.filter((assignment) => {
                    return moment(assignment.Date).day() === rankedRecipe.DayOfWeek;
                });
            }
            //Will only be a favorite if BOTH the day of week AND recipeId match
            const isCustomerFavorite = customerFavorites === null || customerFavorites === void 0 ? void 0 : customerFavorites.find((cf) => cf.DayOfWeek === rankedRecipe.DayOfWeek && cf.recipeId === rankedRecipe.recipeId);
            //If the recipe has no dayOfWeek but the recipeId matches,
            const isFavoriteDuplicate = customerFavorites === null || customerFavorites === void 0 ? void 0 : customerFavorites.find((cf) => cf.recipeId === rankedRecipe.recipeId);
            //Its not a favorite but the its same recipe so it needs its assignments filtered so it only displays on non favorite days
            if (Boolean(!isCustomerFavorite && isFavoriteDuplicate)) {
                assignments = assignments === null || assignments === void 0 ? void 0 : assignments.filter((assignment) => {
                    var _a;
                    return moment(assignment.Date).day() !==
                        ((_a = customerFavorites === null || customerFavorites === void 0 ? void 0 : customerFavorites.find((cf) => cf.recipeId === rankedRecipe.recipeId)) === null || _a === void 0 ? void 0 : _a.DayOfWeek);
                });
            }
            rankedRecipesWithInfo.push(Object.assign(Object.assign({}, currentRecipeInfo), { 
                //This is set and used in the useRecipeModule hook
                AvailableOn: "", Assignments: assignments
                    ? assignments.map((assignment) => {
                        return Object.assign(Object.assign({}, assignment), { RecipeAssignmentPrice: assignment.RecipeAssignmentPrice, AutomaticReplacements: automaticReplacements, CompatibilityType: compatibilityType, Conflicts: recipeIngredientConflicts, Skips: recipeIngredientSkips });
                    })
                    : [], AvailableQuantity: 0, MealTypeIds: (_a = currentRecipeInfo === null || currentRecipeInfo === void 0 ? void 0 : currentRecipeInfo.MealTypeIds) !== null && _a !== void 0 ? _a : [], NutritionMacro: (_b = currentRecipeInfo === null || currentRecipeInfo === void 0 ? void 0 : currentRecipeInfo.NutritionMacro) !== null && _b !== void 0 ? _b : {}, NutritionalLabelImageUrl: (_c = currentRecipeInfo === null || currentRecipeInfo === void 0 ? void 0 : currentRecipeInfo.NutritionalLabelImageUrl) !== null && _c !== void 0 ? _c : "", RecipeImageUrls: (_d = currentRecipeInfo === null || currentRecipeInfo === void 0 ? void 0 : currentRecipeInfo.RecipeImageUrls) !== null && _d !== void 0 ? _d : [], Omissions: (_e = currentRecipeInfo === null || currentRecipeInfo === void 0 ? void 0 : currentRecipeInfo.Omissions) !== null && _e !== void 0 ? _e : [], OriginalQuantity: 0, ProgramMealId: (_g = (_f = currentRecipeInfo === null || currentRecipeInfo === void 0 ? void 0 : currentRecipeInfo.Assignments[0]) === null || _f === void 0 ? void 0 : _f.ProgramMealId) !== null && _g !== void 0 ? _g : -1, Quantity: 0, RecipeDescription: (_h = currentRecipeInfo === null || currentRecipeInfo === void 0 ? void 0 : currentRecipeInfo.RecipeDescription) !== null && _h !== void 0 ? _h : "", RecipeId: (_j = currentRecipeInfo === null || currentRecipeInfo === void 0 ? void 0 : currentRecipeInfo.RecipeId) !== null && _j !== void 0 ? _j : -1, RecipeImageUrl: (_k = currentRecipeInfo === null || currentRecipeInfo === void 0 ? void 0 : currentRecipeInfo.RecipeImageUrl) !== null && _k !== void 0 ? _k : "", RecipeIngredients: updateIngredientOptions(currentRecipeInfo === null || currentRecipeInfo === void 0 ? void 0 : currentRecipeInfo.RecipeIngredients), RecipeName: (_l = currentRecipeInfo === null || currentRecipeInfo === void 0 ? void 0 : currentRecipeInfo.RecipeName) !== null && _l !== void 0 ? _l : "", RecipePrice: (_m = currentRecipeInfo === null || currentRecipeInfo === void 0 ? void 0 : currentRecipeInfo.RecipePrice) !== null && _m !== void 0 ? _m : 0, RecipeShortName: (_o = currentRecipeInfo === null || currentRecipeInfo === void 0 ? void 0 : currentRecipeInfo.RecipeShortName) !== null && _o !== void 0 ? _o : "", RecipeTypeNames: (_p = currentRecipeInfo === null || currentRecipeInfo === void 0 ? void 0 : currentRecipeInfo.RecipeTypeNames) !== null && _p !== void 0 ? _p : [""], ChangeRank: (_q = rankedRecipe.changeRank) !== null && _q !== void 0 ? _q : 0, RecipeFlags: (_r = currentRecipeInfo === null || currentRecipeInfo === void 0 ? void 0 : currentRecipeInfo.RecipeFlags) !== null && _r !== void 0 ? _r : [], RecipeIngredientActions: recipeIngredientActions }));
        });
        const mealsWithRecipeSelections = orderModel.Meals.filter((om) => om.RecipeId);
        const selectedRecipes = getAutoSelectedRecipes(mealsWithRecipeSelections, rankedRecipesWithInfo);
        dispatch({ type: ActionTypes.SET_SELECTED_RECIPES, payload: selectedRecipes });
        // use ADD_RECIPES instead of SET_RECIPES in case multiple recipe modules are on same page
        // otherwise second module to finish would clobber over first
        dispatch({
            type: ActionTypes.ADD_RECIPES,
            payload: rankedRecipesWithInfo,
        });
        if (shouldAutoSelectRecipes) {
            setMealsWithSelectedRecipes(dispatch, cartMeals, selectedRecipes, mealsWithRecipeSelections);
        }
        else {
            dispatch({ type: ActionTypes.SET_MEAL_SELECTION_STATUS, payload: { completed: true } });
        }
    })
        .catch((error) => {
        var _a;
        datadogLog((_a = error.message) !== null && _a !== void 0 ? _a : "Error fetching recipes", LogLevel.ERROR, {}, error);
        console.error("Error fetching recipes:", error);
        dispatch({ type: ActionTypes.SET_RECIPE_FETCH_STATUS, payload: { completed: true, error: true } });
    });
};
/**
 * Assigns recipes to each meal that have recipes auto selected
 *
 * @param dispatch
 * @param cartMeals - User's current meals
 * @param autoSelectedRecipes - Selected Recipes from the API
 * @param mealsWithRecipeSelections - Meals with Recipe changes from the API
 */
export const setMealsWithSelectedRecipes = (dispatch, cartMeals, autoSelectedRecipes, mealsWithRecipeSelections) => {
    dispatch({ type: ActionTypes.SET_MEAL_SELECTION_STATUS, payload: { completed: false } });
    const mealsWithSelectedRecipes = cartMeals.map((meal) => {
        var _a;
        const selectedRecipesForMeal = autoSelectedRecipes === null || autoSelectedRecipes === void 0 ? void 0 : autoSelectedRecipes.filter((recipe) => {
            var _a;
            return (_a = recipe.Assignments) === null || _a === void 0 ? void 0 : _a.find((assignment) => moment(assignment.Date).isSame(meal.MealDate) && assignment.ProgramMealId === meal.ProgramMealId);
        });
        if (selectedRecipesForMeal) {
            let assignments = [];
            selectedRecipesForMeal.map((recipe) => {
                var _a, _b;
                let selectedIngredientOptions = [];
                const assignmentForDay = (_a = recipe.Assignments) === null || _a === void 0 ? void 0 : _a.find((assignment) => moment(assignment.Date).isSame(recipe.AvailableOn));
                const recipePrice = (_b = assignmentForDay === null || assignmentForDay === void 0 ? void 0 : assignmentForDay.RecipeAssignmentPrice) !== null && _b !== void 0 ? _b : 0;
                const existingAssignment = assignments === null || assignments === void 0 ? void 0 : assignments.find((assignment) => assignment.RecipeId === (recipe === null || recipe === void 0 ? void 0 : recipe.RecipeId) &&
                    assignment.ProgramMealId === (recipe === null || recipe === void 0 ? void 0 : recipe.ProgramMealId) &&
                    moment(assignment.Date).isSame(recipe.AvailableOn));
                // Get all recipe changes for the current meal
                let orderMealsToApplyCustomization = mealsWithRecipeSelections === null || mealsWithRecipeSelections === void 0 ? void 0 : mealsWithRecipeSelections.filter((orderMeal) => orderMeal.ProgramMealId == meal.ProgramMealId &&
                    moment(orderMeal.MealDate).isSame(meal.MealDate) &&
                    orderMeal.RecipeId === recipe.RecipeId).flatMap((ri) => ri.RecipeChanges);
                orderMealsToApplyCustomization = removeDuplicatesFromArray(orderMealsToApplyCustomization !== null && orderMealsToApplyCustomization !== void 0 ? orderMealsToApplyCustomization : []);
                if ((orderMealsToApplyCustomization === null || orderMealsToApplyCustomization === void 0 ? void 0 : orderMealsToApplyCustomization.length) > 0) {
                    orderMealsToApplyCustomization.forEach((change) => {
                        var _a, _b, _c, _d, _e, _f, _g;
                        if (change === undefined || !change.MustBeReplaced || change.RecipeId !== recipe.RecipeId)
                            return;
                        let isAddOnIngredient = false;
                        const baseIngredient = (_a = recipe.RecipeIngredients) === null || _a === void 0 ? void 0 : _a.find((i) => i.IngredientId === change.IngredientOriginalId);
                        const replacementIngredient = (_b = baseIngredient === null || baseIngredient === void 0 ? void 0 : baseIngredient.IngredientOptions) === null || _b === void 0 ? void 0 : _b.find((i) => {
                            const isNoneMatch = i.IngredientId === NONE_INGREDIENT_ID;
                            const isExactMatch = i.IngredientId === change.IngredientReplacementId &&
                                i.Quantity === change.ReplacementQty &&
                                i.Unit === change.Unit;
                            return isNoneMatch || isExactMatch;
                        });
                        if (baseIngredient) {
                            isAddOnIngredient =
                                baseIngredient.IngredientOptionMinimum === 0 && baseIngredient.IngredientOptionMaximum > 1;
                        }
                        const existingOption = selectedIngredientOptions === null || selectedIngredientOptions === void 0 ? void 0 : selectedIngredientOptions.find((i) => i.originalIngredientId === (change === null || change === void 0 ? void 0 : change.IngredientOriginalId));
                        const newOption = {
                            replacementIngredientId: (_c = change === null || change === void 0 ? void 0 : change.IngredientReplacementId) !== null && _c !== void 0 ? _c : NONE_INGREDIENT_ID,
                            replacementIngredientOptionId: replacementIngredient === null || replacementIngredient === void 0 ? void 0 : replacementIngredient.IngredientOptionId,
                            replacementIngredientName: (_e = (_d = replacementIngredient === null || replacementIngredient === void 0 ? void 0 : replacementIngredient.Name) !== null && _d !== void 0 ? _d : replacementIngredient === null || replacementIngredient === void 0 ? void 0 : replacementIngredient.IngredientName) !== null && _e !== void 0 ? _e : change === null || change === void 0 ? void 0 : change.IngredientReplacementName,
                            replacementPrice: (_f = change === null || change === void 0 ? void 0 : change.ReplacementPrice) !== null && _f !== void 0 ? _f : 0,
                            Unit: change.Unit,
                            ReplacementQty: change.ReplacementQty,
                        };
                        if (existingOption) {
                            existingOption.selectedOptions.push(newOption);
                        }
                        else {
                            selectedIngredientOptions === null || selectedIngredientOptions === void 0 ? void 0 : selectedIngredientOptions.push({
                                mustBeReplaced: (_g = change === null || change === void 0 ? void 0 : change.MustBeReplaced) !== null && _g !== void 0 ? _g : false,
                                originalIngredientName: change === null || change === void 0 ? void 0 : change.IngredientOriginalName,
                                originalIngredientId: change.IngredientOriginalId,
                                recipeIngredientId: change.RecipeIngredientId,
                                // Add On ingredients do not have default values
                                selectedOptions: !isAddOnIngredient ? [newOption] : [],
                            });
                        }
                    });
                }
                selectedIngredientOptions = removeDuplicatesFromArray(selectedIngredientOptions);
                const newMealAssignment = {
                    RecipeId: recipe.RecipeId,
                    RecipeName: recipe.RecipeName,
                    RecipeShortName: recipe.RecipeShortName,
                    ProgramMealId: recipe.ProgramMealId,
                    Date: moment(recipe.AvailableOn),
                    SelectedIngredientOptions: selectedIngredientOptions,
                    AutomaticReplacements: assignmentForDay === null || assignmentForDay === void 0 ? void 0 : assignmentForDay.AutomaticReplacements,
                    RecipePrice: recipePrice,
                    IsAutomaticSelection: true,
                    Skips: assignmentForDay === null || assignmentForDay === void 0 ? void 0 : assignmentForDay.Skips,
                    RecipeAssignmentPrice: recipePrice,
                    AssignmentId: createKey(),
                    Quantity: recipe.Quantity,
                    AvailableQuantity: assignmentForDay === null || assignmentForDay === void 0 ? void 0 : assignmentForDay.AvailableQuantity,
                };
                if (existingAssignment) {
                    //If there is already an assignment we add that to the recipe quantity to get the total amount
                    const index = assignments === null || assignments === void 0 ? void 0 : assignments.findIndex((a) => a.RecipeId === recipe.RecipeId &&
                        a.ProgramMealId === recipe.ProgramMealId &&
                        moment(a.Date).isSame(recipe.AvailableOn));
                    if (index !== -1) {
                        assignments[index].Quantity += recipe.Quantity;
                    }
                }
                else {
                    assignments.push(Object.assign({}, newMealAssignment));
                }
                selectedIngredientOptions = [];
            });
            meal.RecipeAssignments = assignments;
        }
        meal.RecipeAssignments = (_a = meal.RecipeAssignments) === null || _a === void 0 ? void 0 : _a.map((ra) => {
            return Object.assign(Object.assign({}, ra), { SelectedIngredientOptions: removeDuplicatesFromArray(ra.SelectedIngredientOptions) });
        });
        return meal;
    });
    dispatch({ type: ActionTypes.SET_MEALS, payload: mealsWithSelectedRecipes });
    dispatch({ type: ActionTypes.SET_MEAL_SELECTION_STATUS, payload: { completed: true } });
};
/**
 * Dispatches an action to clear all recipe selections or remove all meals for a
 * given programMealId on the selectedDay
 *
 * @param cartMeals
 * @param dispatch
 * @returns
 */
export const clearRecipeSelections = (cartMeals, dispatch) => (selectedDay, templateQuantity, removeMeals, programMealId) => (e) => {
    dispatch({
        type: ActionTypes.SET_MEALS,
        payload: cartMeals
            .map((meal) => {
            if (meal.ProgramMealId === programMealId && moment(meal.MealDate).isSame(moment(selectedDay))) {
                if (removeMeals) {
                    meal.NumRemovedAssignments = templateQuantity;
                    meal.Quantity = 0;
                }
                else {
                    meal.Quantity = templateQuantity;
                }
                meal.RecipeAssignments = [];
            }
            return meal;
        })
            .filter((meal) => removeMeals || (!removeMeals && meal.Quantity > 0)),
    });
};
/**
 * Helper function to find the auto selected recipes.
 *
 * @param meals
 * @param recipes
 * @returns
 */
const getAutoSelectedRecipes = (meals, recipes) => {
    const selectedRecipes = [];
    const orderMeals = [];
    //Get unique meal dates
    const mealDates = [...new Set(meals.map((meal) => meal.MealDate))];
    //Get unique program & recipe ID pairs
    const addedIds = new Set();
    const programMealAndRecipeIds = [];
    meals.forEach((meal) => {
        const idHash = `${meal.ProgramMealId}|${meal.RecipeId}`;
        if (!addedIds.has(idHash)) {
            programMealAndRecipeIds.push({
                ProgramMealId: meal.ProgramMealId,
                RecipeId: meal.RecipeId,
            });
        }
        addedIds.add(idHash);
    });
    //If RecipeAssignment Resource Lookup is RoundRobin, it will assign different recipes to fill the meal assignments.
    //If RecipeAssignment Resource Lookup is OptimalOnly, it will fill the meal assignments with a single recipe.
    //In the OptimalOnly case, each assignment is separate recipe object so we have to combine them to get the total quantity.
    mealDates.forEach((mealDate) => {
        programMealAndRecipeIds.forEach((ids) => {
            const duplicateRecipes = meals.filter((meal) => meal.RecipeId === ids.RecipeId &&
                moment(meal.MealDate).isSame(mealDate) &&
                ids.ProgramMealId === meal.ProgramMealId);
            const targetMeal = meals === null || meals === void 0 ? void 0 : meals.find((meal) => meal.RecipeId === ids.RecipeId &&
                moment(meal.MealDate).isSame(mealDate) &&
                ids.ProgramMealId === meal.ProgramMealId);
            if (targetMeal) {
                //If the program meal/recipe combo has more than one duplicate, the quantity needs to be coalesced
                if (duplicateRecipes.length > 1) {
                    const totalQuantity = duplicateRecipes.reduce((acc, meal) => acc + meal.Quantity, 0);
                    const mealRecipeComboExists = orderMeals === null || orderMeals === void 0 ? void 0 : orderMeals.find((meal) => meal.ProgramMealId === (targetMeal === null || targetMeal === void 0 ? void 0 : targetMeal.ProgramMealId) &&
                        meal.RecipeId === (targetMeal === null || targetMeal === void 0 ? void 0 : targetMeal.RecipeId) &&
                        moment(meal.MealDate).isSame(mealDate));
                    if (!mealRecipeComboExists) {
                        orderMeals.push(Object.assign(Object.assign({}, targetMeal), { Quantity: totalQuantity }));
                    }
                }
                else {
                    orderMeals.push(targetMeal);
                }
            }
        });
    });
    orderMeals.forEach((orderMeal) => {
        var _a, _b;
        //Find the matching recipe that has an assignment for this meal for this day
        const targetRecipe = recipes === null || recipes === void 0 ? void 0 : recipes.find((recipe) => {
            var _a;
            return recipe.RecipeId === orderMeal.RecipeId &&
                ((_a = recipe.Assignments) === null || _a === void 0 ? void 0 : _a.find((assignment) => assignment.ProgramMealId === orderMeal.ProgramMealId && moment(orderMeal.MealDate).isSame(assignment.Date)));
        });
        if (targetRecipe) {
            const assignment = (_a = targetRecipe.Assignments) === null || _a === void 0 ? void 0 : _a.find((assignment) => assignment.ProgramMealId === orderMeal.ProgramMealId && moment(orderMeal.MealDate).isSame(assignment.Date));
            const doesRecipeExistForDay = selectedRecipes === null || selectedRecipes === void 0 ? void 0 : selectedRecipes.find((r) => r.RecipeId === (assignment === null || assignment === void 0 ? void 0 : assignment.RecipeId) && moment(r.AvailableOn).isSame(assignment === null || assignment === void 0 ? void 0 : assignment.Date));
            if (!doesRecipeExistForDay)
                selectedRecipes.push(Object.assign(Object.assign({}, targetRecipe), { ProgramMealId: (_b = assignment === null || assignment === void 0 ? void 0 : assignment.ProgramMealId) !== null && _b !== void 0 ? _b : -1, Quantity: orderMeal === null || orderMeal === void 0 ? void 0 : orderMeal.Quantity, RecipePrice: targetRecipe === null || targetRecipe === void 0 ? void 0 : targetRecipe.RecipePrice, AvailableOn: moment(assignment === null || assignment === void 0 ? void 0 : assignment.Date).format(), Assignments: assignment ? [assignment] : [] }));
        }
    });
    return selectedRecipes;
};
/**
 * Helper function for fetching recipes.
 *
 * If the user selected a recipe we need to attach that recipeId to the meal to persist the user's selections.
 * If it was an automatic selection we dont set the recipeId so the API makes another automatic selection.
 *
 * If there are no meals in the cart and the flow does not have the MQM or the MBC the user cannot select meals.
 * In this case we have to use the programs meals from the recipe module to fetch recipes.
 * @param cartMeals
 * @returns Meal array used to fetch recipes
 */
export const getMealsWithSelectionSource = (cartMeals, allProgramMeals, mealSize, allMealSizeOption, recipeSelectionType, userCannotChooseMeals, recipeModuleProgramMeals, dayFilterDays, explodeOMDD, useAutoSelection, onlyShowDeliveryDays) => {
    // MT-5253
    // when only showing delivery days and explode OMDD resource lookup is false,
    // recipe auto assignment should assign meals according to their actual meal day
    // (eg. all Monday meals assigned same recipe, all Tuesday meals assigned same recipe, etc.)
    // Meal.CoalescedMealDates contains the actual meal days that do not appear in cart when only showing delivery days
    const useMealDayAutoSelection = !!useAutoSelection && !!onlyShowDeliveryDays && !explodeOMDD;
    const meals = [];
    const filteredProgramMeals = allProgramMeals.filter((programMeal) => {
        if (mealSize === undefined ||
            (mealSize === null || mealSize === void 0 ? void 0 : mealSize.Size) === "" ||
            (programMeal === null || programMeal === void 0 ? void 0 : programMeal.MealSize) === (mealSize === null || mealSize === void 0 ? void 0 : mealSize.Size) ||
            (mealSize === null || mealSize === void 0 ? void 0 : mealSize.Size) === allMealSizeOption) {
            return programMeal;
        }
    });
    const programMealsToFetch = recipeModuleProgramMeals === null || recipeModuleProgramMeals === void 0 ? void 0 : recipeModuleProgramMeals.filter((pmId) => filteredProgramMeals === null || filteredProgramMeals === void 0 ? void 0 : filteredProgramMeals.find((pm) => pm.ProgramMealId === pmId));
    if (!cartMeals.length && userCannotChooseMeals) {
        dayFilterDays === null || dayFilterDays === void 0 ? void 0 : dayFilterDays.forEach((day) => {
            programMealsToFetch === null || programMealsToFetch === void 0 ? void 0 : programMealsToFetch.forEach((id) => {
                meals.push({
                    MealDate: moment(day).format("YYYY-MM-DDTHH:mm:ss"),
                    Quantity: 0,
                    ProgramMealId: id,
                });
            });
        });
    }
    else {
        cartMeals.forEach((meal) => {
            var _a, _b, _c, _d, _e;
            const userSelections = (_a = meal.RecipeAssignments) === null || _a === void 0 ? void 0 : _a.filter((assignment) => !assignment.IsAutomaticSelection ||
                assignment.SelectedIngredientOptions.filter((selection) => selection.mustBeReplaced).length > 0);
            const autoSelections = (_b = meal.RecipeAssignments) === null || _b === void 0 ? void 0 : _b.filter((assignment) => assignment.IsAutomaticSelection &&
                assignment.SelectedIngredientOptions.filter((selection) => selection.mustBeReplaced).length === 0);
            if (!((_c = meal.RecipeAssignments) === null || _c === void 0 ? void 0 : _c.length)) {
                meals.push({
                    MealDate: moment(meal.MealDate).format("YYYY-MM-DDTHH:mm:ss"),
                    CoalescedMealDates: meal.CoalescedMealDates,
                    Quantity: meal.Quantity,
                    ProgramMealId: meal.ProgramMealId,
                });
            }
            if (autoSelections && autoSelections.length) {
                processSelections(meals, meal, autoSelections, true);
            }
            if (userSelections && userSelections.length) {
                processSelections(meals, meal, userSelections);
            }
            const totalUserSelection = (_d = userSelections === null || userSelections === void 0 ? void 0 : userSelections.reduce((total, assignment) => total + assignment.Quantity, 0)) !== null && _d !== void 0 ? _d : 0;
            const totalAutoSelection = (_e = autoSelections === null || autoSelections === void 0 ? void 0 : autoSelections.reduce((total, assignment) => total + assignment.Quantity, 0)) !== null && _e !== void 0 ? _e : 0;
            const totalSelections = totalUserSelection + totalAutoSelection;
            const qtyToCompare = totalSelections > 0 ? totalSelections : meal.Quantity;
            const remainingAssignmentsToFill = meal.Quantity - qtyToCompare;
            if (remainingAssignmentsToFill > 0) {
                const remainingMeal = {
                    MealDate: moment(meal.MealDate).format("YYYY-MM-DDTHH:mm:ss"),
                    CoalescedMealDates: meal.CoalescedMealDates,
                    Quantity: remainingAssignmentsToFill,
                    ProgramMealId: meal.ProgramMealId,
                };
                meals.push(remainingMeal);
            }
        });
    }
    //If recipe type selection is ADD or BOTH we have to show recipes for all program meals
    if (recipeSelectionType === RecipeSelectionType.Both || recipeSelectionType === RecipeSelectionType.AddMeals) {
        const mealsNotSelected = programMealsToFetch === null || programMealsToFetch === void 0 ? void 0 : programMealsToFetch.filter((id) => !(meals === null || meals === void 0 ? void 0 : meals.find((meal) => meal.ProgramMealId === id)));
        dayFilterDays === null || dayFilterDays === void 0 ? void 0 : dayFilterDays.forEach((day) => {
            mealsNotSelected === null || mealsNotSelected === void 0 ? void 0 : mealsNotSelected.forEach((id) => {
                meals.push({
                    MealDate: moment(day).format("YYYY-MM-DDTHH:mm:ss"),
                    Quantity: 0,
                    ProgramMealId: id,
                });
            });
        });
    }
    //Meals with a quantity > 1 need to be split. This allows multiple recipes to be auto selected for a meal
    const separateMeals = meals.flatMap((meal) => {
        if (meal.Quantity > 1) {
            // with meal day auto selection, we want to split out the meals into meal day groupings
            // so that each grouping is assigned a different recipe by round robin
            // meal.CoalescedMealDates contains the meal days
            if (useMealDayAutoSelection && meal.CoalescedMealDates && meal.CoalescedMealDates.length > 1) {
                const mealsPerDay = Math.floor(meal.Quantity / meal.CoalescedMealDates.length);
                let extraMeals = meal.Quantity % meal.CoalescedMealDates.length;
                let splitMeals = meal.CoalescedMealDates.map((mealDate) => {
                    let dayQuantity = mealsPerDay;
                    if (extraMeals > 0) {
                        dayQuantity += 1;
                        extraMeals -= 1;
                    }
                    return {
                        ProgramMealId: meal.ProgramMealId,
                        MealDate: meal.MealDate,
                        Quantity: dayQuantity,
                        RecipeId: meal.RecipeId,
                        RecipeChanges: meal.RecipeChanges,
                    };
                });
                return splitMeals;
            }
            else {
                return Array(meal.Quantity).fill({
                    ProgramMealId: meal.ProgramMealId,
                    MealDate: meal.MealDate,
                    Quantity: 1,
                    RecipeId: meal.RecipeId,
                    RecipeChanges: meal.RecipeChanges,
                });
            }
        }
        else {
            return meal;
        }
    });
    return separateMeals;
};
const recipeChangesFromAssignment = (assignment) => {
    const recipeChanges = [];
    assignment.SelectedIngredientOptions.forEach((option) => {
        option.selectedOptions.forEach((selectedOption) => {
            var _a, _b;
            let change = {
                IngredientOriginalName: (_a = option.originalIngredientName) !== null && _a !== void 0 ? _a : "",
                IngredientReplacementName: (_b = selectedOption.replacementIngredientName) !== null && _b !== void 0 ? _b : "",
                OrderMealId: undefined,
                RecipeId: assignment.RecipeId,
                ReplacementQty: selectedOption.ReplacementQty,
                IngredientOriginalId: option.originalIngredientId,
                RecipeIngredientId: option.recipeIngredientId,
                MustBeReplaced: option.mustBeReplaced,
                IngredientReplacementId: selectedOption.replacementIngredientId,
                ReplacementPrice: selectedOption.replacementPrice,
                IngredientSkip: false,
                Unit: selectedOption.Unit,
            };
            recipeChanges.push(change);
        });
    });
    return recipeChanges;
};
const processSelections = (meals, meal, selections, useMealDayAutoSelection = false) => {
    selections.forEach((assignment) => {
        meals.push({
            MealDate: moment(meal.MealDate).format("YYYY-MM-DDTHH:mm:ss"),
            CoalescedMealDates: meal.CoalescedMealDates,
            Quantity: assignment.Quantity,
            ProgramMealId: assignment.ProgramMealId,
            RecipeId: useMealDayAutoSelection ? undefined : assignment.RecipeId,
            RecipeChanges: recipeChangesFromAssignment(assignment),
        });
    });
};
/**
 * Makes ingredient substitutions and tracks conflicting ingredients
 *
 * @param recipeIngredientActions
 * @param shouldReplaceIngredients
 * @param currentRecipe
 * @returns
 */
const makeIngredientSubstitutions = (recipeIngredientActions, shouldReplaceIngredients, currentRecipe) => {
    const conflicts = [];
    const replacements = [];
    const skips = [];
    recipeIngredientActions.map((ingredientAction) => {
        var _a, _b;
        //Skip is enabled for a recipe, that means it does not need to be replaced
        if (ingredientAction.Skip && shouldReplaceIngredients) {
            const skip = {
                OriginalIngredientId: ingredientAction.OriginalIngredientId,
                RecipeIngredientId: ingredientAction.RecipeIngredientId,
                MustBeReplaced: ingredientAction.IngredientMustReplace,
            };
            skips.push(skip);
            return;
        }
        const shouldBeConflict = !shouldReplaceIngredients ||
            (ingredientAction.SubIngredientId1 === null && ingredientAction.SubIngredientId2 === null);
        // If an ingredient is a must replace it cannot be a conflict or an auto replacement
        if (shouldBeConflict && !ingredientAction.IngredientMustReplace) {
            conflicts.push(ingredientAction.OriginalIngredientId);
            return;
        }
        else if (!ingredientAction.IngredientMustReplace) {
            const replacement = {
                RecipeId: currentRecipe.RecipeId,
                RecipeIngredientId: ingredientAction.RecipeIngredientId,
                OriginalIngredient: {
                    id: ingredientAction.OriginalIngredientId,
                    name: ingredientAction.OriginalIngredientName,
                },
                ReplacementIngredient: {
                    id: (_a = ingredientAction.SubIngredientId1) !== null && _a !== void 0 ? _a : ingredientAction.SubIngredientId2,
                    name: (_b = ingredientAction.SubIngredientName1) !== null && _b !== void 0 ? _b : ingredientAction.SubIngredientName2,
                },
                MustBeReplaced: ingredientAction.IngredientMustReplace,
                Unit: ingredientAction.Unit,
                ReplacementQty: ingredientAction.Quantity
            };
            replacements.push(replacement);
        }
    });
    return { conflicts, replacements, skips };
};
/**
 * Check if the recipe has any ingredient actions.
 *
 * @param recipeIngredientActions
 * @param shouldReplaceIngredients
 * @param currentRecipeInfo
 * @returns
 */
export const checkReplacementsAndConflicts = (recipeIngredientActions, shouldReplaceIngredients, currentRecipeInfo, omissions) => {
    let recipeIngredientConflicts = [];
    let recipeIngredientSkips = [];
    let automaticReplacements = [];
    let compatibilityType = undefined;
    let isConflict = false;
    let isModified = false;
    if (recipeIngredientActions === null || recipeIngredientActions === void 0 ? void 0 : recipeIngredientActions.length) {
        const { replacements, conflicts, skips } = makeIngredientSubstitutions(recipeIngredientActions, shouldReplaceIngredients, currentRecipeInfo);
        recipeIngredientConflicts = conflicts;
        recipeIngredientSkips = skips;
        automaticReplacements = replacements;
    }
    if (omissions) {
        const { ingredientsToReplace } = getMustReplaceIngredients(currentRecipeInfo);
        if (ingredientsToReplace) {
            ingredientsToReplace.forEach((ing) => {
                var _a;
                let incompatibleIngredientCount = 0;
                for (const option of ing.IngredientOptions) {
                    const conflictingIngredientOption = (_a = option.IngredientMajorRelations) === null || _a === void 0 ? void 0 : _a.find((id) => omissions === null || omissions === void 0 ? void 0 : omissions.includes(id));
                    if (conflictingIngredientOption) {
                        //If this ingredient option contains a matching dislike, it's incompatible with user omissions. count it.
                        recipeIngredientConflicts.push(conflictingIngredientOption);
                        incompatibleIngredientCount++;
                    }
                }
                //If all ingredient options contains a matching dislike, recipe should show as conflicted.
                isConflict = incompatibleIngredientCount === ing.IngredientOptions.length;
                //If at least one ingredient option contains a matching dislike, recipe should show as modified.
                isModified = incompatibleIngredientCount > 0;
            });
        }
    }
    isConflict = recipeIngredientConflicts.length > 0;
    isModified = automaticReplacements.length > 0 || recipeIngredientSkips.length > 0;
    compatibilityType = isConflict
        ? RecipeIngredientCompatibility.CONFLICT
        : isModified
            ? RecipeIngredientCompatibility.MODIFIED
            : RecipeIngredientCompatibility.MATCH;
    return { recipeIngredientConflicts, automaticReplacements, recipeIngredientSkips, compatibilityType };
};
/**
 * Returns all MustReplace ingredients for a given recipe.
 * An ingredient is considered a MustReplace if it has ingredient options.
 *
 * @param {Recipe} [recipe] - The recipe object containing ingredient details.
 * @returns {{ ingredientsToReplace: RecipeIngredient[] }} An object containing a list of ingredients that require replacements.
 * If no recipe is provided, it returns an empty list of `ingredientsToReplace`.
 */
export const getMustReplaceIngredients = (recipe) => {
    const ingredientsToReplace = recipe === null || recipe === void 0 ? void 0 : recipe.RecipeIngredients.filter((ri) => ri.MustReplace);
    return {
        ingredientsToReplace,
    };
};
/**
 * If the ingredient is optional and does not have a None ingredient option we have to add one.
 * If the ingredient options do contain a None option the ID needs to be updated.
 *
 *
 * @param ingredients
 * @returns
 */
export const updateIngredientOptions = (ingredients) => {
    if (!ingredients) {
        return [];
    }
    return ingredients.map((ingredient) => {
        var _a, _b, _c;
        if (ingredient.IngredientOptionMinimum === 0 && ingredient.IngredientOptionMaximum === 1) {
            const doesNoneOptionExist = (_a = ingredient.IngredientOptions) === null || _a === void 0 ? void 0 : _a.find((io) => io.IngredientId === null || io.IngredientId === NONE_INGREDIENT_ID);
            if (!doesNoneOptionExist) {
                (_b = ingredient.IngredientOptions) === null || _b === void 0 ? void 0 : _b.unshift({
                    IngredientMajorRelations: [],
                    IngredientName: "None",
                    IngredientOptionSeq: 0,
                    IngredientPrice: 0,
                    IngredientPriceDisplay: "$0.00",
                    IngredientType: "",
                    IsDefaultOption: false,
                    IngredientId: NONE_INGREDIENT_ID,
                    IngredientOptionId: NONE_INGREDIENT_ID,
                    Name: null,
                    Unit: null,
                    Quantity: null
                });
            }
        }
        return Object.assign(Object.assign({}, ingredient), { IngredientOptions: (_c = ingredient.IngredientOptions) === null || _c === void 0 ? void 0 : _c.sort((a, b) => a.IngredientOptionSeq - b.IngredientOptionSeq).map((ingOption) => {
                return Object.assign(Object.assign({}, ingOption), { 
                    // None ingredient option has no id so we have set it
                    // Without an id it will not be selectable on the customization form
                    IngredientId: (ingOption === null || ingOption === void 0 ? void 0 : ingOption.IngredientId) === null ? NONE_INGREDIENT_ID : ingOption === null || ingOption === void 0 ? void 0 : ingOption.IngredientId });
            }) });
    });
};
